import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import Logo from "./Logo";
import { BRAND, NAV_LINKS } from "@/lib/site";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      style={{ top: "var(--ab-height, 0px)" }}
      className={`fixed inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-navy-900/10 shadow-sm"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="container-x h-20 flex items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-10" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.testid}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isActive
                    ? "text-navy-900 font-medium"
                    : "text-navy-500 hover:text-navy-900"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={BRAND.phoneHref}
            data-testid="nav-phone-link"
            className="hidden md:inline-flex items-center gap-2 text-navy-900 hover:text-gold-500 transition-colors text-sm font-medium"
          >
            <Phone className="h-4 w-4" strokeWidth={1.75} />
            {BRAND.phone}
          </a>
          <Link
            to="/contact"
            data-testid="nav-cta-consultation"
            className="btn-primary text-sm px-5 py-2.5"
          >
            Free Consultation
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-full border border-navy-900/15 text-navy-900 hover:bg-navy-900 hover:text-white transition-colors"
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-300 ${
          open ? "max-h-[480px] border-t border-navy-900/10" : "max-h-0"
        }`}
      >
        <div className="container-x py-6 flex flex-col gap-5 bg-white">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`mobile-${l.testid}`}
              className={({ isActive }) =>
                `text-lg font-serif ${
                  isActive ? "text-gold-500" : "text-navy-900"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <div className="flex flex-col gap-3 pt-3 border-t border-navy-900/10">
            <a
              href={BRAND.phoneHref}
              data-testid="mobile-phone-link"
              className="inline-flex items-center gap-2 text-navy-900 font-medium"
            >
              <Phone className="h-4 w-4" strokeWidth={1.75} />
              {BRAND.phone}
            </a>
            <Link
              to="/contact"
              data-testid="mobile-cta-consultation"
              className="btn-primary w-full"
            >
              Free Consultation
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
